import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
    combineLatest,
    delay,
    EMPTY,
    filter,
    fromEvent,
    map,
    of,
    startWith,
    switchMap,
    take,
    withLatestFrom,
} from 'rxjs';

import { Registration } from 'app/features/manual-input-registration/models';
import {
    selectAppModeIfDefined,
    selectScanFlowRegistrationSubmitIntervalMsIfDefined,
} from 'app/features/settings/state/settings/settings.selectors';
import {
    scanFlowRegistrationDataStorageKey,
    scanFlowScheduledRegistrationDateStorageKey,
    StorageService,
} from 'app/shared/services';
import { hoursToMilliseconds } from 'app/shared/utils';
import {
    addRegistrationsToCache,
    showingToastAfterRegistration,
} from 'app/state/registrations/registrations.actions';
import { selectUserSessionEmployeeNumber } from 'app/state/user-session/user-session.selectors';

import { getActivityCode, getBatchCode } from '../../../utils/scanned-code-list.utils';
import { createScannedCodeFromActivity } from '../../scanned-codes/utils';
import { sendCurrentScanFlowContainerMeasurementData } from '../scan-flow-result-registration/scan-flow-result-registration.actions';
import { isResultRegistrationActivity } from '../scan-flow-result-registration/scan-flow-result-registration.utils';
import {
    applyScanFlowRegistrationDataFromStorage,
    initScanFlow,
    initScanFlowSuccess,
    setActivityInScanFlow,
    showNotificationScanFlowInProgressRegistrationDataSent,
} from '../scan-flow.actions';
import { emptyScanFlowRegistrationData, ScanFlowRegistrationData } from '../scan-flow.reducer';
import {
    mapScanFlowCodesToActivity,
    selectScanFlowActivity,
    selectScanFlowInitializationProgress,
    selectScanFlowRegistrationData,
} from '../scan-flow.selectors';
import { getNextActivityAfterRegistration } from '../scan-flow.utils';
import {
    cancelScheduledScanFlowRegistration,
    scheduleScanFlowRegistration,
    sendCurrentScanFlowRegistrationData,
    sendTransformedScanFlowRegistrationData,
    sendTransformedScanFlowRegistrationDataSuccess,
} from './scan-flow-labor-registration.actions';
import {
    mapToScanFlowRegistrationDataCompleteness,
    selectScanFlowAutoRegistrationAndCompleteness,
    transformScanFlowRegistrationData,
} from './scan-flow-labor-registration.selectors';

function getIncompleteRegistrationIntervalInMs({
    registrationSubmitIntervalInMs,
}: {
    registrationSubmitIntervalInMs: number;
}): number {
    return Math.max(120000, registrationSubmitIntervalInMs + 60000);
}

/**
    Effects for the scan flow labor registration feature
    This includes initialization, handling registration data submission and store/cancel scheduling
*/

// noinspection JSUnusedGlobalSymbols
@Injectable()
export class ScanFlowLaborRegistrationEffects {
    onInitSetRegistrationData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initScanFlow),
            withLatestFrom(this.store.select(selectScanFlowInitializationProgress)),
            filter(([, { done }]) => !done),
            switchMap(() => {
                const partialDataFromStorage = this.storageService.get<Partial<ScanFlowRegistrationData>>(
                    scanFlowRegistrationDataStorageKey,
                );

                const registrationDataFromStorage: ScanFlowRegistrationData | undefined =
                    partialDataFromStorage
                        ? {
                              ...emptyScanFlowRegistrationData,
                              ...partialDataFromStorage,
                          }
                        : undefined;

                return this.store.pipe(
                    mapToScanFlowRegistrationDataCompleteness(registrationDataFromStorage),
                    take(1),
                    map((registrationDataCompleteness) => ({
                        registrationDataFromStorage,
                        registrationDataCompleteness,
                    })),
                );
            }),

            switchMap(({ registrationDataFromStorage, registrationDataCompleteness }) => {
                const scheduledRegistrationDateFromStorage = this.storageService.get<number>(
                    scanFlowScheduledRegistrationDateStorageKey,
                );
                /**
                   Get registration data from local storage
                   If scheduled registration date exists and is in the past, send the registration data now
                   Otherwise, apply the registration data from storage to the scan flow state
               */
                if (
                    scheduledRegistrationDateFromStorage &&
                    scheduledRegistrationDateFromStorage < Date.now()
                ) {
                    const onSuccessAction =
                        registrationDataCompleteness === 'in-progress'
                            ? showNotificationScanFlowInProgressRegistrationDataSent()
                            : undefined;

                    return registrationDataFromStorage
                        ? [
                              sendTransformedScanFlowRegistrationData({
                                  registrationData: registrationDataFromStorage,
                                  onSuccessAction,
                                  registrationTimestamp: scheduledRegistrationDateFromStorage,
                              }),
                              initScanFlowSuccess(),
                          ]
                        : [initScanFlowSuccess()];
                }

                return [applyScanFlowRegistrationDataFromStorage({ registrationDataFromStorage })];
            }),
        ),
    );

    sendCurrentScanFlowRegistrationData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sendCurrentScanFlowRegistrationData),
            withLatestFrom(this.store.select(selectScanFlowRegistrationData)),
            map(([{ registrationTimestamp }, registrationData]) => {
                return sendTransformedScanFlowRegistrationData({
                    registrationData,
                    registrationTimestamp,
                    onSuccessAction: undefined,
                });
            }),
        ),
    );

    sendTransformedScanFlowRegistrationData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sendTransformedScanFlowRegistrationData),
            switchMap(({ registrationData, registrationTimestamp, onSuccessAction }) =>
                this.store.pipe(
                    transformScanFlowRegistrationData({
                        registrationData,
                        registrationTimestamp,
                    }),
                    take(1),
                    filter((registration): registration is Registration => !!registration),
                    map((registration) => ({ registration, registrationData, onSuccessAction })),
                ),
            ),
            switchMap(({ registration, registrationData, onSuccessAction }) => {
                let nextActions: Action[] = [
                    addRegistrationsToCache({ registrations: [registration] }),
                    showingToastAfterRegistration({ registrations: [registration] }),
                    sendTransformedScanFlowRegistrationDataSuccess({ registrationData }),
                ];

                if (onSuccessAction) nextActions = [...nextActions, onSuccessAction];

                return nextActions;
            }),
        ),
    );

    setNextActivityAfterRegistrationDataSubmitted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sendTransformedScanFlowRegistrationDataSuccess),
            switchMap(({ registrationData }) =>
                this.store.pipe(
                    mapScanFlowCodesToActivity(registrationData.scannedCodes),
                    map((registrationDataActivity) => ({
                        registrationData,
                        registrationDataActivity,
                    })),
                    take(1),
                ),
            ),
            withLatestFrom(
                this.store.select(selectUserSessionEmployeeNumber),
                this.store.pipe(selectAppModeIfDefined),
            ),
            map(
                ([{ registrationData, registrationDataActivity }, userSessionEmployeeNumber, appMode]):
                    | Action
                    | undefined => {
                    const noEmployeeNumberInRegistrationData = !registrationData.employeeNumber;
                    const userSessionEmployeeNumberInvalid =
                        noEmployeeNumberInRegistrationData ||
                        userSessionEmployeeNumber !== registrationData.employeeNumber;

                    if (
                        (appMode === 'personal' && userSessionEmployeeNumberInvalid) ||
                        (appMode === 'multi-user' && noEmployeeNumberInRegistrationData)
                    ) {
                        return undefined;
                    }

                    const newActivity = registrationDataActivity
                        ? getNextActivityAfterRegistration({
                              currentActivity: registrationDataActivity,
                              appMode,
                          })
                        : undefined;

                    const currentActivityCode = getActivityCode(registrationData.scannedCodes);
                    const timeRegistrationActivity = currentActivityCode?.systemCode?.isSystemCode
                        ? getActivityCode(registrationData.scannedCodes)
                        : undefined;

                    const newActivityCode = newActivity
                        ? (timeRegistrationActivity ?? createScannedCodeFromActivity(newActivity))
                        : undefined;

                    // If there's activity is the same after registration, we want to keep the batch number
                    const currentBatchCode =
                        newActivity && registrationData.batchNumber
                            ? getBatchCode(registrationData.scannedCodes)
                            : undefined;

                    return setActivityInScanFlow({
                        activityCode: newActivityCode,
                        keepRegistrationData: false,
                        isContainerMeasurement: isResultRegistrationActivity(newActivity),
                        batchCode: currentBatchCode,
                    });
                },
            ),
            filter((action): action is Action => !!action),
        ),
    );

    scheduleRegistration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initScanFlowSuccess, applyScanFlowRegistrationDataFromStorage),
            switchMap(() => this.store.pipe(selectScanFlowAutoRegistrationAndCompleteness)),
            withLatestFrom(
                this.store.pipe(selectScanFlowRegistrationSubmitIntervalMsIfDefined),
                this.store.pipe(selectScanFlowActivity),
            ),
            map(
                ([
                    { registrationDataCompleteness, isAutoRegistrationEnabled },
                    registrationSubmitIntervalInMs,
                    activity,
                ]) => {
                    if (!isAutoRegistrationEnabled || registrationDataCompleteness === 'empty') {
                        return cancelScheduledScanFlowRegistration();
                    }

                    // By default, incomplete registrations are scheduled after 2 minutes
                    // For packing activities, we want to schedule the registration sent after 1 hour
                    // This allows more time for the user to complete the registration / if user forgot to click on "Finish"
                    const incompleteRegistrationIntervalInMs = isResultRegistrationActivity(activity)
                        ? hoursToMilliseconds(1)
                        : getIncompleteRegistrationIntervalInMs({ registrationSubmitIntervalInMs });
                    const submitIntervalInMs =
                        registrationDataCompleteness === 'complete'
                            ? registrationSubmitIntervalInMs
                            : incompleteRegistrationIntervalInMs;

                    return scheduleScanFlowRegistration({
                        scheduledRegistrationDate: Date.now() + submitIntervalInMs,
                    });
                },
            ),
        ),
    );

    sendScheduledRegistration$ = createEffect(() =>
        combineLatest([
            this.actions$.pipe(ofType(scheduleScanFlowRegistration, cancelScheduledScanFlowRegistration)),
            this.store.pipe(selectScanFlowActivity),
            // important when tab is inactive/closed
            fromEvent(document, 'visibilitychange').pipe(startWith(1)),
        ]).pipe(
            switchMap(([action, activity]) => {
                if (isResultRegistrationActivity(activity)) {
                    return 'scheduledRegistrationDate' in action
                        ? of(
                              sendCurrentScanFlowContainerMeasurementData({
                                  registrationTimestamp: action.scheduledRegistrationDate!,
                              }),
                          ).pipe(delay(action.scheduledRegistrationDate! - Date.now()))
                        : EMPTY;
                } else {
                    return 'scheduledRegistrationDate' in action
                        ? of(
                              sendCurrentScanFlowRegistrationData({
                                  registrationTimestamp: action.scheduledRegistrationDate!,
                              }),
                          ).pipe(delay(action.scheduledRegistrationDate! - Date.now()))
                        : EMPTY;
                }
            }),
        ),
    );

    putScheduledRegistrationDateToStorage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(scheduleScanFlowRegistration),
                map(({ scheduledRegistrationDate }) => {
                    this.storageService.store(
                        scanFlowScheduledRegistrationDateStorageKey,
                        scheduledRegistrationDate,
                    );
                    return true;
                }),
            ),
        { dispatch: false },
    );

    clearScheduledRegistrationDateFromStorage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cancelScheduledScanFlowRegistration),
                map(() => {
                    this.storageService.clear(scanFlowScheduledRegistrationDateStorageKey);
                    return true;
                }),
            ),
        { dispatch: false },
    );

    constructor(
        private actions$: Actions,
        private store: Store,
        private storageService: StorageService,
    ) {}
}
